/**
 * Accès réseau de base : JSON avec réessais, limitation de parallélisme,
 * lecture prudente des nombres venus d'une API.
 */
import { REQUEST_TIMEOUT_MS, REQUEST_TRIES } from '../core/constants';

export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Récupère du JSON avec réessais et délai maximum.
 *
 * `cache: 'no-store'` est volontaire : une réponse météo mise en cache par le
 * navigateur ferait afficher un air d'il y a une heure comme s'il était
 * actuel, ce qui est exactement le défaut que cette application doit éviter.
 */
export async function getJson<T>(url: string): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt < REQUEST_TRIES; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return (await response.json()) as T;
    } catch (error) {
      lastError = error;
      // Attente croissante : 600 ms puis 1,2 s. Inutile d'insister plus vite
      // qu'un service momentanément saturé ne se rétablit.
      if (attempt < REQUEST_TRIES - 1) await delay(600 * 2 ** attempt);
    } finally {
      clearTimeout(timer);
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

/**
 * Exécute au plus `limit` tâches à la fois, en préservant l'ordre des résultats.
 *
 * Neuf images de plein format en parallèle saturaient la mémoire du
 * navigateur et faisaient échouer les décodages, ce qui ressemblait à une
 * panne réseau alors que c'était une panne de mémoire.
 */
export async function pool<T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;

  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await task(items[index]!, index);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker()),
  );
  return results;
}

/** Un nombre exploitable, ou `null`. Écarte `undefined`, `NaN` et l'infini. */
export function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Convertit un horodatage naïf d'Open-Meteo en instant absolu.
 *
 * L'API renvoie des chaînes sans fuseau, du type "2026-07-27T13:00", plus un
 * décalage `utc_offset_seconds`. Les passer directement à `new Date()` les
 * ferait interpréter dans le fuseau du NAVIGATEUR : correct depuis la France,
 * faux de plusieurs heures depuis ailleurs, ce qui décalerait d'autant les
 * âges de données et les heures d'arrivée de fumée.
 *
 * On les lit donc comme de l'UTC, puis on retire le décalage annoncé.
 */
export function epochFromNaiveLocal(
  iso: string | undefined,
  utcOffsetSeconds: number,
): number | null {
  if (!iso) return null;
  const withSeconds = /T\d{2}:\d{2}$/.test(iso) ? `${iso}:00` : iso;
  const asUtc = Date.parse(`${withSeconds}Z`);
  return Number.isFinite(asUtc) ? asUtc - utcOffsetSeconds * 1000 : null;
}
